import React from "react"
import { Button } from "reactstrap"
import { USER_TYPES } from "helpers/sf_constants"

// MILO = "Machine Intelligence for Lead Optimization" (Pricing Advisor in the backend). The model
// behind it is never named in the UI (SI-10, D6): reason codes are shown as plain text, and the
// raw model text only appears under "Technical details".

export const MILO_FULL_NAME = "Machine Intelligence for Lead Optimization"

// MILO shows pricing data for every account, so only the Admin and Super Admin roles may open it
// (sidebar link and page). Matched by role name: role ids differ per environment.
const MILO_ROLES = [USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]
export const canSeeMilo = (adminUser) => MILO_ROLES.includes(adminUser?.admin_role?.name?.trim().toLowerCase())

export const MILO_PAGE_SIZE = 25

export const MILO_REASON_TEXT = {
    BEHIND_PACE_RAISE: "Behind its daily goal, so MILO suggests a higher price to win more leads.",
    BEHIND_PACE_HOLD: "Behind, but a higher price is not expected to help right now. Price kept.",
    ON_PACE_HOLD: "On track to fill the daily cap. Price kept.",
    LOW_CONFIDENCE_HOLD: "MILO was not sure enough that a change would help. Price kept.",
    LOW_DATA_HOLD: "Not enough activity yet today to judge. Price kept.",
    NEAR_CAP_HOLD: "The daily cap is almost used. Price kept.",
    NOT_PRICE_LIMITED_HOLD: "Leads are limited by the delivery pace (throttle) setting, not by price. Price kept.",
    // No longer produced by the backend; old rows can still carry it.
    AHEAD_OF_PACE_EASE: "Ahead of pace. Price kept.",
}

export const MILO_REASON_SHORT = {
    BEHIND_PACE_RAISE: "Behind pace - raise",
    BEHIND_PACE_HOLD: "Behind pace - hold",
    ON_PACE_HOLD: "On pace",
    LOW_CONFIDENCE_HOLD: "Not sure it helps",
    LOW_DATA_HOLD: "Too little data",
    NEAR_CAP_HOLD: "Cap almost used",
    NOT_PRICE_LIMITED_HOLD: "Limited by throttle",
    AHEAD_OF_PACE_EASE: "Ahead of pace",
}

// An unknown code still gets a readable label instead of a blank cell.
export const getReasonShort = (code) => (code ? (MILO_REASON_SHORT[code] || `Other (${code})`) : "—")
export const getReasonText = (code) => (code ? (MILO_REASON_TEXT[code] || `Other reason: ${code}`) : null)

// Staff see plain statuses. The API's decision_type is finer (a MILO suggestion and a platform
// hold can both mean "keep the price"), so the status is worked out in getMiloStatus. Each label
// is also the value the list API filters on.
export const MILO_STATUSES = {
    raise: { label: "Raise", badge: "badge-soft-success", dot: "bg-success" },
    hold: { label: "Hold", badge: "badge-soft-secondary", dot: "bg-secondary" },
    failed: { label: "Failed", badge: "badge-soft-danger", dot: "bg-danger" },
    reject: { label: "Reject", badge: "badge-soft-warning", dot: "bg-warning" },
    // A campaign or check with pacing data but no recorded decision.
    none: { label: "No recommendation", badge: "badge-soft-dark", dot: "bg-dark" },
}

export const MILO_STATUS_OPTIONS = [
    { label: "All statuses", value: "" },
    ...Object.keys(MILO_STATUSES).map(status => ({ label: MILO_STATUSES[status].label, value: status })),
]

export const MILO_REASON_OPTIONS = [
    { label: "All reasons", value: "" },
    ...Object.keys(MILO_REASON_SHORT)
        .filter(code => code !== "AHEAD_OF_PACE_EASE")
        .map(code => ({ label: MILO_REASON_SHORT[code], value: code })),
]

export const MILO_DEFAULT_SORT = { sort_column: "decision_at", sort_direction: "desc" }

// The API may send decimals as strings ("0.0" is truthy in JS); always go through Number().
const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

// Both models only raise or keep the price (dial >= 100), so anything that is not a raise is a
// hold. Old AHEAD_OF_PACE_EASE rows also kept the price.
export const getMiloStatus = (decisionType, dial) => {
    if (decisionType === "failed") return "failed"
    if (decisionType === "rejected") return "reject"
    if (!decisionType || decisionType === "no_recommendation") return "none"
    return toNumber(dial) > 100 ? "raise" : "hold"
}

export const formatDial = (dial) => {
    const n = toNumber(dial)
    if (n === null) return "—"
    if (n === 100) return "No change"
    const diff = Math.round((n - 100) * 10) / 10
    return `${diff > 0 ? "+" : ""}${diff}%`
}

export const formatMoney = (value) => {
    const n = toNumber(value)
    return n === null ? "—" : `$${n.toFixed(2)}`
}

export const formatShare = (value) => {
    const n = toNumber(value)
    return n === null ? "—" : `${Math.round(n * 100)}%`
}

export const formatRatio = (value) => {
    const n = toNumber(value)
    return n === null ? "—" : n.toFixed(2)
}

// Pacing is measured on the account's local day, so times are shown in that zone (not PST).
export const formatLocalTime = (iso, timeZone, withDate = false) => {
    if (!iso) return "—"
    try {
        return new Date(iso).toLocaleString("en-US", {
            timeZone,
            month: withDate ? "short" : undefined,
            day: withDate ? "numeric" : undefined,
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
        })
    } catch (e) {
        return iso
    }
}

export const formatCapValue = (pacing) => {
    if (!pacing?.has_cap) return "No cap"
    const used = toNumber(pacing.used)
    const cap = toNumber(pacing.cap_value)
    if (pacing.cap_type === "dollar") return `${formatMoney(used)} of ${formatMoney(cap)}`
    return `${used} of ${cap} leads`
}

export const DecisionBadge = ({ type, dial }) => {
    const config = MILO_STATUSES[getMiloStatus(type, dial)]
    return <span className={`badge ${config.badge} font-size-12`}>{config.label}</span>
}

export const PaceBadge = ({ pacing }) => {
    if (!pacing?.has_cap) return <span className="text-muted">—</span>
    if (pacing.pace_status === "behind") {
        return <span className="badge badge-soft-danger font-size-12">Behind · {formatRatio(pacing.pace_ratio)}</span>
    }
    if (pacing.pace_status === "on_pace") {
        return <span className="badge badge-soft-success font-size-12">On pace · {formatRatio(pacing.pace_ratio)}</span>
    }
    return <span className="text-muted" title="Too early in the day to measure pace">—</span>
}

export const CapUsedBar = ({ pacing, compact = false }) => {
    if (!pacing?.has_cap) return <span className="text-muted">No cap</span>
    const used = Math.min(100, Math.round((toNumber(pacing.used_share) || 0) * 100))
    const expected = Math.min(100, Math.round((toNumber(pacing.day_elapsed_share) || 0) * 100))
    return (
        <div style={{ minWidth: compact ? 110 : 200 }} title={`${used}% used, ${expected}% expected by now`}>
            <div className="d-flex justify-content-between font-size-12">
                <span>{used}%</span>
                {!compact && <span className="text-muted">expected {expected}%</span>}
            </div>
            <div className="progress position-relative" style={{ height: 6 }}>
                <div className={`progress-bar ${pacing.pace_status === "behind" ? "bg-danger" : "bg-success"}`} style={{ width: `${used}%` }} />
                <div className="position-absolute bg-dark" style={{ left: `${expected}%`, top: -2, width: 2, height: 10 }} />
            </div>
        </div>
    )
}

export const SuggestionCell = ({ decision }) => {
    if (!decision || decision.dial_percent === null || decision.dial_percent === undefined) {
        return <span className="text-muted">—</span>
    }
    const dial = toNumber(decision.dial_percent)
    return (
        <span>
            <span className={dial > 100 ? "fw-semibold text-success" : ""}>{formatDial(dial)}</span>
            {decision.validation_status === "clamped" && (
                <span className="badge badge-soft-info ms-1" title="The safety limit cut the step down">Limited</span>
            )}
        </span>
    )
}

// Column dataFields double as the backend sort keys, so they must match the server whitelist.
export const miloCampaignColumns = (onOpenCampaign) => [
    {
        dataField: "campaign_id",
        text: "Campaign",
        sort: true,
        formatter: (cell, row) => (
            <div>
                <Button color="link" className="p-0 fw-semibold text-start" onClick={() => onOpenCampaign(row)}>
                    {row.campaign_name}
                </Button>
                <div className="text-muted font-size-12">CID {row.campaign_id} · AID {row.account_id} · {row.account_name}</div>
            </div>
        ),
    },
    {
        dataField: "decision_type",
        text: "Status",
        formatter: (cell, row) => <DecisionBadge type={cell} dial={row.latest_decision?.dial_percent} />,
    },
    {
        dataField: "dial_percent",
        text: "Suggestion",
        sort: true,
        formatter: (cell, row) => <SuggestionCell decision={row.latest_decision} />,
    },
    {
        dataField: "reason_code",
        text: "Reason",
        formatter: (cell, row) => {
            const code = row.latest_decision?.reason_code
            return <span title={getReasonText(code) || ""}>{getReasonShort(code)}</span>
        },
    },
    {
        // Current, suggested (estimate) and applied price share one column to keep the table narrow.
        dataField: "current_price",
        text: "Price",
        sort: true,
        formatter: (cell, row) => (
            <div>
                <span>{formatMoney(row.prices?.current_price)}</span>
                {row.prices?.suggested_price !== null && row.prices?.suggested_price !== undefined &&
                    row.prices.suggested_price !== row.prices.current_price && (
                    <span className="fst-italic text-muted" title="Estimate: current price × suggestion">
                        {" → "}{formatMoney(row.prices.suggested_price)} est.
                    </span>
                )}
                <div className="text-muted font-size-12" title="Prices are not applied in Phase 1">Applied —</div>
            </div>
        ),
    },
    {
        dataField: "used_share",
        text: "Cap used",
        sort: true,
        formatter: (cell, row) => <CapUsedBar pacing={row.pacing} compact />,
    },
    {
        dataField: "pace_ratio",
        text: "Pace",
        sort: true,
        formatter: (cell, row) => <PaceBadge pacing={row.pacing} />,
    },
    {
        // The API's name for the latest decision time.
        dataField: "decision_at",
        text: "Last checked",
        sort: true,
        formatter: (cell, row) => {
            const at = row.latest_decision?.decided_at
            const timeZone = row.pacing?.timezone
            return (
                <div>
                    <div>{formatLocalTime(at, timeZone)}</div>
                    <div className="text-muted font-size-12">{at ? new Date(at).toLocaleDateString("en-US", { timeZone, month: "short", day: "numeric" }) : ""}</div>
                </div>
            )
        },
    },
]
